import useFocusTrap from '@charlietango/use-focus-trap';
import { Action, ActionKind, Icon } from '@dynamic/ui';
import React, { FC, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import { CSSTransition } from 'react-transition-group';
import styled, { css } from 'styled-components';

import { LogoMvpd } from '#/components/Logo/LogoMvpd';
import { useActions } from '#/hooks/actions/useActions';
import { useConfig } from '#/hooks/useConfig';
import { useNavigation } from '#/hooks/useNavigation';
import { useOnLocationChange } from '#/hooks/useOnLocationChange';
import { useIsMobile } from '#/hooks/useResponsiveValue';
import { useScrollControl } from '#/hooks/useScrollControl';
import { THamburgerItemsData } from '#/service/config/common.types';
import { MenuFocusStyle } from '#/service/constants';
import { InternalPageId } from '#/service/constants/menu';
import { remify } from '#/service/utils';
import { tb } from '#/store/translation/translation.store';

import {
  fadeInAnimation,
  fadeOutAnimation,
  slideInLeftAnimation,
  slideOutLeftAnimation,
} from '../styles';
import {
  linkStyle,
  MenuGradient,
  MenuNav,
  transparentModeStyles,
  transProps,
} from './components';
import { ItemLink } from './ItemLink';
import { MenuProps } from './Menu.types';
import {
  findMenuItemByView,
  getDefaultMenuItemId,
  isLoginAction,
} from './Menu.utils';

const showIcons = true;
const menuContentsId = 'hamburger-menu-contents';
const HAMBURGER_TRANSITION_MS = 300;

interface MenuLogoProps {
  isCollapsed?: boolean;
  isEmpty?: boolean;
  src: string;
  alt: string;
}

const HamburgerMenuNav = styled(MenuNav)`
  inline-size: calc(100% - ${remify(12)});
  height: ${remify(80)};
  padding-left: ${remify(6)};
  padding-right: ${remify(6)};
  ${transparentModeStyles}
`;

HamburgerMenuNav.displayName = 'HamburgerMenuNav';

const MenuLogo = styled.img.attrs(
  (props): MenuLogoProps => ({
    src: props.theme.logo?.primary ?? '',
    alt: tb('tts.app.logo'),
  })
)`
  ${({ isEmpty }) => css`
    padding: 0 ${isEmpty ? remify(16) : 0};
  `}
  inset: 0 0 ${remify(80)} 0;
  margin-block: auto auto;
  ${(props) => !props.src && 'display: none;'}
  max-inline-size: ${remify(180)};
  max-height: ${remify(60)};
  max-width: ${remify(100)};
  transition: bottom ${transProps};
  cursor: pointer;
  ${(props) =>
    props.isCollapsed &&
    css`
      bottom: 0;
    `};
`;

MenuLogo.displayName = 'MenuLogo';

interface MenuItemsContainerProps {
  $isMobile?: boolean;
}

const MenuItemsContainer = styled.div<MenuItemsContainerProps>`
  display: flex;
  flex-direction: column;
  gap: ${remify(16)};
  block-size: 100%;
  background-color: var(--menuOpaqueBackground);
  box-sizing: border-box;
  padding: ${remify(16)};
  pointer-events: auto;

  ${({ $isMobile }) =>
    $isMobile
      ? css`
          inline-size: 100%;
        `
      : css`
          max-inline-size: ${remify(348)};
        `}

  > button:first-child {
    align-self: flex-start;
    margin-left: ${remify(-10)};
    margin-top: ${remify(-16)};
  }

  > div {
    padding-bottom: 0;
  }

  & .box .icon {
    height: 1.3rem;
    width: 1.3rem;
    margin-right: ${remify(24)};
  }
`;

MenuItemsContainer.displayName = 'MenuItemsContainer';

const MenuButton = styled.button`
  background: none;
  border: none;
  outline: none;
  height: 100%;
  ${linkStyle};
  padding: 0 ${remify(25)}!important;
  cursor: pointer;
`;

MenuButton.displayName = 'MenuButton';

const MenuOverlay = styled.div`
  position: absolute;
  top: var(--bannerHeight, 0);
  left: 0;
  right: 0;
  height: calc(100vh - var(--bannerHeight, 0rem));
  background-color: var(--menuGradientBackground);
  pointer-events: auto;

  &.enter {
    animation: ${fadeInAnimation} ${HAMBURGER_TRANSITION_MS}ms ease-in-out
      forwards;

    ${MenuItemsContainer} {
      animation: ${slideInLeftAnimation} ${HAMBURGER_TRANSITION_MS}ms
        ease-in-out forwards;
    }
  }

  &.exit {
    animation: ${fadeOutAnimation} ${HAMBURGER_TRANSITION_MS}ms ease-in-out
      forwards;

    ${MenuItemsContainer} {
      animation: ${slideOutLeftAnimation} ${HAMBURGER_TRANSITION_MS}ms
        ease-in-out forwards;
    }
  }
`;

MenuOverlay.displayName = 'MenuOverlay';

const UserProfileDiv = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${remify(16)};
  margin-block-end: ${remify(30)};
`;

UserProfileDiv.displayName = 'UserProfileDiv';

const IconsContainer = styled.div`
  display: flex;
  align-items: center;
  height: 100%;
  padding: 0;
  margin-left: auto;

  & > a,
  button {
    padding: 0 ${remify(24)};
  }
`;

IconsContainer.displayName = 'IconsContainer';

const preventClick = (e: React.MouseEvent<HTMLDivElement>) => {
  e.stopPropagation();
};

interface HamburgerButtonProps {
  onClick: () => void;
  focusStyle?: MenuFocusStyle;
  isOpen: boolean;
}

const HamburgerButton = ({
  isOpen,
  onClick,
  focusStyle,
}: HamburgerButtonProps) => {
  const icon = isOpen ? 'line_3_horizontal' : 'cross';
  const translationKey = isOpen ? 'button.openMenu' : 'button.closeMenu';

  return (
    <MenuButton
      $focusStyle={focusStyle}
      aria-controls={menuContentsId}
      aria-expanded={isOpen}
      aria-haspopup={true}
      aria-label={tb(translationKey)}
      data-testid="hamburger-toggle"
      onClick={onClick}
    >
      <Icon key="icon" name={icon} />
    </MenuButton>
  );
};

const HamburgerItems = ({
  mainMenuItems = [],
  linkMenuItems = [],
  showItems = false,
  toggleHamburger,
  focusStyle,
}: THamburgerItemsData) => {
  const { actions } = useActions();
  const isMobile = useIsMobile();
  const defaultSelectedItem = mainMenuItems[0]?.id;
  const menuRef = useFocusTrap(showItems);
  const overlayRef = useRef<HTMLDivElement | null>(null);

  const setOverlayRef = (node: HTMLDivElement | null) => {
    overlayRef.current = node;
    menuRef(node);
  };

  const filteredIconItems = linkMenuItems.filter(
    (item) =>
      ![InternalPageId.Search, InternalPageId.More].includes(
        item.action?.parameters?.view ?? ''
      )
  );

  // If there is a login button in the main menu, we must make it open login popup.
  // there is no page to route to
  const getCallback = (action: Action) => {
    toggleHamburger();

    if (isLoginAction(action)) {
      actions[ActionKind.Login]?.();
    }
  };

  const content = (
    <MenuOverlay
      data-testid="hamburger-menu-overlay"
      onClick={toggleHamburger}
      ref={setOverlayRef}
    >
      <MenuItemsContainer
        $isMobile={isMobile}
        data-testid="hamburger-menu-item-container"
        id={menuContentsId}
        onClick={preventClick}
      >
        <HamburgerButton
          focusStyle={focusStyle}
          isOpen={false}
          onClick={toggleHamburger}
        />
        {mainMenuItems.map((item) => (
          <ItemLink
            defaultSelectedItem={defaultSelectedItem}
            inHamburger
            item={item}
            key={item.id}
            onClick={toggleHamburger}
            showIcons={showIcons}
          />
        ))}
        <UserProfileDiv>
          {filteredIconItems.map((item) => (
            <ItemLink
              defaultSelectedItem={defaultSelectedItem}
              inHamburger
              item={item}
              key={item.id}
              onClick={() => getCallback(item?.action)}
              showIcons={showIcons}
            />
          ))}
        </UserProfileDiv>
        {isMobile && <LogoMvpd isCentered />}
      </MenuItemsContainer>
    </MenuOverlay>
  );

  // mobile has no animation
  if (isMobile) {
    return showItems ? content : null;
  }

  return (
    <CSSTransition
      classNames={{ enter: 'enter', exit: 'exit' }}
      in={showItems}
      mountOnEnter
      nodeRef={overlayRef}
      timeout={HAMBURGER_TRANSITION_MS}
      unmountOnExit
    >
      {content}
    </CSSTransition>
  );
};

export const HamburgerMenu: FC<MenuProps> = (props) => {
  const {
    mainMenuItems = [],
    linkMenuItems = [],
    isCollapsed = false,
    isHidden = false,
    isTransparent,
  } = props;

  const { goHome } = useNavigation('HamburgerMenu');
  const [showItems, setShowItems] = useState(false);
  const { enableScroll, disableScroll } = useScrollControl();
  const defaultMenuItemId = getDefaultMenuItemId(mainMenuItems);
  const {
    theme: {
      menu: { focus: focusStyle },
    },
  } = useConfig();
  const isMobile = useIsMobile();
  const { pathname } = useLocation();

  // close when the location changes
  useOnLocationChange(() => {
    if (showItems) {
      setShowItems(false);
      enableScroll();
    }
  });

  const toggleHamburger = () => {
    (showItems ? enableScroll : disableScroll)();
    setShowItems(!showItems);
  };

  const extraItems = [
    findMenuItemByView(linkMenuItems, InternalPageId.Search),
    findMenuItemByView(linkMenuItems, InternalPageId.More),
  ].filter(Boolean);

  // Hide the menu on the playback page.
  if (pathname.startsWith('/playback')) {
    return null;
  }

  // when there are no menu items, we shouldn't show the hamburger button
  const isEmpty = mainMenuItems.length + linkMenuItems.length === 0;

  return (
    <>
      <HamburgerMenuNav
        $isCollapsed={isCollapsed}
        $isHidden={isHidden}
        $isTransparent={isTransparent}
        data-testid="menu-nav"
      >
        <MenuGradient />
        {!isEmpty && !isTransparent && (
          <HamburgerButton
            focusStyle={focusStyle}
            isOpen
            onClick={toggleHamburger}
          />
        )}
        <MenuLogo
          isCollapsed={isCollapsed}
          isEmpty={isEmpty || isTransparent}
          onClick={goHome}
        />
        <IconsContainer>
          {!isMobile && <LogoMvpd />}
          {extraItems.map(
            (item) =>
              item && (
                <ItemLink
                  defaultSelectedItem={defaultMenuItemId}
                  item={item}
                  key={item.id}
                  showIcons={showIcons}
                />
              )
          )}
        </IconsContainer>
      </HamburgerMenuNav>
      <HamburgerItems
        focusStyle={focusStyle}
        linkMenuItems={linkMenuItems}
        mainMenuItems={mainMenuItems}
        showItems={showItems}
        toggleHamburger={toggleHamburger}
      />
    </>
  );
};
